/**
 * familyStore — Firestore helpers for the `families` collection.
 *
 * A Group is a named entity that collects multiple Contacts. Two types:
 *   family       — biological family or household
 *   organization — work company, firm, or professional group
 *
 * A Contact can belong to multiple groups. Each membership carries an optional
 * relationship label (e.g. "husband", "CEO", "partner").
 *
 * Data model
 * ──────────
 *  Firestore collection: `families`
 *  Each doc shape: FamilyGroup (see interface below)
 *
 * Contact denorm
 * ──────────────
 *  After any membership change, `familyIds[]` is batch-written back to each
 *  affected `contacts/{id}` doc so the contact card can show group membership
 *  without a secondary query.
 */
import {
  arrayUnion,
  arrayRemove,
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  where,
  writeBatch,
  type Timestamp,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from './firebase';
import {
  projectLinkedGroupBody,
} from './projectStore';

/* ═══════════════════════════════ Interfaces ════════════════════════════════ */

export interface FamilyMember {
  contactId: string;
  /** Denormalised name for fast display without extra reads. */
  name: string;
  /** Free-text relationship label (e.g. "husband", "CEO"). Optional. */
  relationship?: string;
  /* ── Build C (2026-07-06) — the durable org-axis + kinship shelf ──
   * The structure charts (org chart / family tree) and the directory's
   * role-based lenses need these 3-axis values AT REST on the group member,
   * not only inside the transient intake form. Persisted from the intake
   * persons by `upsertContactsAndGroupFromIntake` (datamap #20/#21). All
   * optional + additive — a legacy / family member simply omits them. The
   * canonical vocabularies live in `types/personModel.ts` (RelationToClient,
   * WorkRole, Department); stored as strings here to keep this store React/
   * Firebase-pure and tolerant of a legacy free-text value.
   */
  /** Axis 2 — kinship relation to the native (RelationToClient value). */
  relation_to_client?: string;
  /** Axis 3a — designation in the organisation (WorkRole value). */
  work_role?: string;
  /**
   * Axis 3b — the org-chart edge: the `contactId` of ANOTHER member in this
   * SAME group that this member reports to. NOTE: intake captures `reports_to`
   * as a transient intake ROW id; the write path maps it to the durable member
   * `contactId` here (self-cycle + stale-ref guarded), so the persisted edge is
   * resolvable on the group without the intake form.
   */
  reports_to?: string;
  /** Axis 3c — functional department (Department value). */
  department?: string;
}

/**
 * Organisation billing details. These are ONLY meaningful when a Group's
 * `type === 'organization'` — a family never carries billing fields. All
 * optional; captured for issuing quotations / releasing payment through the
 * right firm (owner Decision 2, 2026-07-05). Stored flat on the `families` doc.
 */
export interface OrgBilling {
  /** GST registration number of the organisation. */
  gstNumber?: string;
  /** PAN of the organisation. */
  pan?: string;
  /** Billing / registered address. */
  billingAddress?: string;
  /** Billing email of the organisation. */
  orgEmail?: string;
  /** Billing phone of the organisation. */
  orgPhone?: string;
}

/** The five org-billing keys, in canonical order. */
export const ORG_BILLING_KEYS = [
  'gstNumber',
  'pan',
  'billingAddress',
  'orgEmail',
  'orgPhone',
] as const;

/**
 * PURE — build the Firestore write payload for org-billing fields. Returns ONLY
 * the trimmed, non-empty entries (Firestore rejects `undefined` — this store's
 * `db` is initialised without `ignoreUndefinedProperties`), and returns an empty
 * object for non-organization groups so a family never gets billing fields.
 */
function orgBillingWrite(
  type: 'family' | 'organization',
  billing: OrgBilling | undefined,
): Record<string, string> {
  if (type !== 'organization' || !billing) return {};
  const out: Record<string, string> = {};
  for (const k of ORG_BILLING_KEYS) {
    const v = (billing[k] ?? '').trim();
    if (v) out[k] = v;
  }
  return out;
}

export interface FamilyGroup {
  id: string;              // Firestore doc ID (filled in by listenFamilies)
  /**
   * Friendly, human-readable group number (P1 Contacts, 2026-07-06) — the
   * `G-###` label the owner wants on the group header IN PLACE of the raw
   * Firestore doc id (`#<docid>` → `#G-208`). Minted ONCE at group creation
   * from `counters/group_av` (same monotonic-increment pattern as
   * `clients/{cid}.clientNo` / property_av — see `nextGroupSeq` /
   * `groupNoFromSeq`) and never re-minted. Additive: legacy groups created
   * before this build carry no `groupNo` until the one-time backfill sweep
   * (scripts/backfill-group-numbers.ts) assigns them one; the UI must fall
   * back to `#<id>` while it is absent.
   */
  groupNo?: string;
  name: string;            // e.g. "Khandelwal Family" / "Sharma & Associates"
  type: 'family' | 'organization';
  description?: string;
  /**
   * Primary location of the group (P1 Contacts, 2026-07-06) — a free-text
   * locality string, e.g. "Baner, Pune". Applies to families AND organisations
   * (distinct from the org-only `billingAddress`). Optional + additive.
   */
  location?: string;
  /**
   * Billing contact person (P1 Contacts, datamap #19b / Build L, 2026-07-06) —
   * a foreign key to `clients/{cid}`: the Contact responsible for billing on
   * this group. Stores the CONTACT DOC ID (a `C#####`), not a name. Optional +
   * additive; typically (but not necessarily) one of the group's `members`.
   */
  billingContactId?: string;
  members: FamilyMember[];
  /**
   * Astro link — the id of the astro app's family this Group represents.
   * Stamped when an astro family creation direct-writes into `families`
   * (the "one home" architecture; the old manual Sync-from-Astro reconcile
   * pull is retired). A Group with `astroFamilyId` set is "Astro-linked";
   * undefined/null = a plain portal-authored Group.
   */
  astroFamilyId?: string | null;
  /**
   * Display-only cache of the astro family's member count at last sync, kept in
   * step with the real `members[]` roster (the astro members ARE now mirrored
   * into `members[]` — each becomes a real Contact deduped by `astroPersonId`).
   * Retained for the "Astro-linked" badge copy.
   */
  astroMemberCount?: number;
  /**
   * Organisation billing fields — populated ONLY when `type === 'organization'`
   * (a family never carries them). See OrgBilling. Purely additive; legacy /
   * family docs simply omit these keys.
   */
  gstNumber?: string;
  pan?: string;
  billingAddress?: string;
  orgEmail?: string;
  orgPhone?: string;
  /**
   * Dedup suppression (P1 Contacts, 2026-07-06) — the ids of OTHER Groups a
   * human has CONFIRMED are genuinely distinct despite the dedup engine flagging
   * them (a mistyped GSTIN, or two real firms/families with similar names).
   * Written BIDIRECTIONALLY so the suppression is symmetric; the Duplicates tab
   * drops any group pair where either lists the other. Additive.
   */
  notDuplicateOf?: string[];
  /**
   * Recoverable soft-delete (P1 Contacts, 2026-07-06) — mirrors the contact
   * soft-delete (`clients/{id}.trashed`). A `trashed:true` Group is filtered out
   * of `listenFamilies` (so it vanishes from Groups, pickers, dedup, projects),
   * yet the doc survives and can be restored (`restoreFamily`). Set by
   * `trashFamily` (duplicate "Delete one") and by `mergeFamilyInto` on the loser.
   */
  trashed?: boolean;
  trashedAt?: Timestamp | null;
  trashedBy?: string | null;
  /** Set on the loser of a group merge — the survivor Group it folded into. */
  mergedIntoFamilyId?: string;
  createdAt: Timestamp;
  createdBy: string;       // actor name
  updatedAt: Timestamp;
}

/* ═══════════════════════════════ Listener ══════════════════════════════════ */

/**
 * Real-time listener on the full `families` collection. Returns an unsubscribe
 * function. Calls `onUpdate` with the current snapshot on every change.
 */
export function listenFamilies(
  onUpdate: (groups: FamilyGroup[]) => void,
  opts?: { onErr?: () => void },
): Unsubscribe {
  const ref = collection(db, 'families');
  return onSnapshot(
    ref,
    (snap) => {
      const groups: FamilyGroup[] = snap.docs
        // Recoverable soft-delete — a trashed Group is hidden everywhere the
        // live listener feeds (Groups list, pickers, dedup, project links).
        .filter((d) => (d.data() as { trashed?: unknown }).trashed !== true)
        .map((d) => {
        const raw = d.data();
        return {
          ...(raw as Omit<FamilyGroup, 'id'>),
          id: d.id,
          members: Array.isArray(raw.members) ? (raw.members as FamilyMember[]) : [],
          // Defensive hydration of the astro-link fields — older/manual docs
          // won't carry them.
          astroFamilyId: typeof raw.astroFamilyId === 'string' ? raw.astroFamilyId : null,
          astroMemberCount:
            typeof raw.astroMemberCount === 'number' ? raw.astroMemberCount : undefined,
          notDuplicateOf: Array.isArray(raw.notDuplicateOf)
            ? (raw.notDuplicateOf as unknown[]).filter((x): x is string => typeof x === 'string')
            : undefined,
        };
      });
      onUpdate(groups);
    },
    () => opts?.onErr?.(),
  );
}

/**
 * Real-time listener on the SOFT-TRASHED Groups (`families` where
 * `trashed === true`) — the mirror of `listenFamilies`, which filters them OUT.
 * Feeds the Trash view's Groups section so a soft-deleted Group has a real
 * in-app restore path (Vex). Single-field equality → no composite index.
 */
export function listenTrashedFamilies(
  onUpdate: (groups: FamilyGroup[]) => void,
  opts?: { onErr?: () => void },
): Unsubscribe {
  const ref = query(collection(db, 'families'), where('trashed', '==', true));
  return onSnapshot(
    ref,
    (snap) => {
      const groups: FamilyGroup[] = snap.docs.map((d) => {
        const raw = d.data();
        return {
          ...(raw as Omit<FamilyGroup, 'id'>),
          id: d.id,
          members: Array.isArray(raw.members) ? (raw.members as FamilyMember[]) : [],
        };
      });
      onUpdate(groups);
    },
    () => opts?.onErr?.(),
  );
}

/* ══════════════════════ Friendly group number (groupNo) ═════════════════════ */

/** Firestore counter doc id for the group-number series. */
const GROUP_COUNTER_PATH = 'group_av';

/**
 * PURE — format a group serial as the friendly `G-###` label (zero-padded to
 * 3 digits; the `#` UI prefix is added by the view, e.g. `#G-208`). Mirrors the
 * pure `clientNoFromSeq` / `propertyNoFromSeq` format helpers. `padStart` never
 * truncates, so a 4-digit group (`G-1000`) prints in full.
 */
export function groupNoFromSeq(seq: number): string {
  return `G-${String(seq).padStart(3, '0')}`;
}

/**
 * IMPURE — atomically mint the next group serial from `counters/group_av`, the
 * SAME monotonic-increment transaction shape as `nextClientSeq` (client_av) /
 * `nextPropertySeq` (property_av). Seeded at 0 (fresh series — groups carried
 * only their raw doc id before this build), so the first mint is seq 1 → G-001.
 * Max-continuation: it always increments the stored counter, so both this create
 * path AND the one-time backfill sweep can draw from it without ever colliding.
 *
 * RULES: `counters/group_av` requires the shared `counters/{id}` matcher to be
 * widened to include the `group_*` family (today it permits
 * `(client|project|partner|property)_.*`). That rules widen + deploy is a
 * SEPARATE gated step (shared infra — coordinate before deploying), exactly as
 * `property_av` was added in Module 4.
 */
async function nextGroupSeq(): Promise<number> {
  const ref = doc(db, 'counters', GROUP_COUNTER_PATH);
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const cur =
      snap.exists() && typeof snap.data().seq === 'number'
        ? (snap.data().seq as number)
        : 0;
    const next = cur + 1;
    tx.set(ref, { seq: next, updatedAt: serverTimestamp() }, { merge: true });
    return next;
  });
}

/**
 * Mint the next friendly group number (`G-###`). Public for callers that need
 * the number before/without a full create; prefer `createFamily`, which mints +
 * writes it atomically onto the new group doc.
 */
export async function mintGroupNo(): Promise<string> {
  const seq = await nextGroupSeq();
  return groupNoFromSeq(seq);
}

/**
 * FAIL-SOFT mint — returns the next `groupNo`, or `null` if the counter
 * transaction fails for ANY reason (most importantly a rules-denial while the
 * shared `counters/{id}` matcher has not yet been widened to `group_*`). Group
 * creation MUST NOT be coupled to that pending rules deploy: on failure we log
 * and let the caller create the group WITHOUT a `groupNo` (the UI falls back to
 * `#<id>`, and the one-time backfill — or a later create once rules land —
 * numbers it). Deterministic: a failed transaction never partially increments
 * the counter (runTransaction is atomic), so there is no double-count on retry.
 */
async function tryMintGroupNo(): Promise<string | null> {
  try {
    return await mintGroupNo();
  } catch (e) {
    console.warn(
      '[familyStore] groupNo mint failed — creating the group without a number ' +
        '(it will be numbered by the backfill / a later create once counters/group_av ' +
        'rules land):',
      (e as Error).message,
    );
    return null;
  }
}

/* ═══════════════════════════════ Writes ════════════════════════════════════ */

/**
 * Create a new FamilyGroup document. Batch-writes `familyIds` back to all
 * member contacts. Returns the new Firestore doc ID.
 */
export async function createFamily(data: {
  name: string;
  type: 'family' | 'organization';
  description?: string;
  members: FamilyMember[];
  actorName: string;
  /** Org-billing fields — persisted ONLY when `type === 'organization'`. */
  billing?: OrgBilling;
  /** Primary location (free text, e.g. "Baner, Pune"). Optional + additive. */
  location?: string;
}): Promise<string> {
  const { name, type, description, members, actorName, billing, location } = data;

  // Mint the friendly group number ONCE, up-front (transaction — cannot live
  // inside the writeBatch below, same shape as the astro-sync clientNo mint).
  // FAIL-SOFT: a mint failure (e.g. counter rules not yet deployed) must NOT
  // block group creation — the group is created number-less and gets one later.
  const groupNo = await tryMintGroupNo();

  // Use writeBatch: create the family doc + update each contact atomically.
  // Firestore batch limit is 500 ops; groups with hundreds of members are
  // unrealistic for AV use (households / small firms), so a single batch is
  // safe here.
  const batch = writeBatch(db);

  // New family doc ref (auto-id)
  const familyRef = doc(collection(db, 'families'));
  const familyId = familyRef.id;

  const now = serverTimestamp();
  const locationTrimmed = (location ?? '').trim();
  batch.set(familyRef, {
    // Only write `groupNo` when the mint succeeded (Firestore rejects undefined
    // on this db; a null/absent number falls back to `#<id>` in the UI).
    ...(groupNo ? { groupNo } : {}),
    name,
    type,
    description: description ?? '',
    members,
    // Only write `location` when non-empty (Firestore rejects undefined on this
    // db, and an empty string would needlessly occupy the field).
    ...(locationTrimmed ? { location: locationTrimmed } : {}),
    ...orgBillingWrite(type, billing),
    createdAt: now,
    createdBy: actorName,
    updatedAt: now,
  });

  // Back-fill familyIds on each contact doc. `clients/{cid}.familyIds[]` is the
  // ONE denormalised membership mirror (Build I, 2026-07-06 — the abandoned
  // `contacts/` dual-write is retired; that collection was read by nobody).
  // set+merge never fails with "No document to update".
  for (const m of members) {
    const familyIdPayload = { familyIds: arrayUnion(familyId) };
    batch.set(doc(db, 'clients', m.contactId), familyIdPayload, { merge: true });
  }

  await batch.commit();
  return familyId;
}

/**
 * Partial-update a FamilyGroup doc (name, type, description, members…).
 * Batch-writes `familyIds` updates for any contacts whose membership changed
 * (additions or removals implied by a full members-array replacement).
 *
 * NOTE: when updating `members`, pass the FULL new array (not a delta) — this
 * function recomputes the add/remove delta against the previous members list
 * to keep contact `familyIds` in sync.
 */
export async function updateFamily(
  id: string,
  patch: Partial<
    Pick<
      FamilyGroup,
      | 'name'
      | 'type'
      | 'description'
      | 'members'
      | 'location'
      | 'billingContactId'
      | 'gstNumber'
      | 'pan'
      | 'billingAddress'
      | 'orgEmail'
      | 'orgPhone'
    >
  >,
  prevMembers: FamilyMember[],
  actorName: string,
): Promise<void> {
  const batch = writeBatch(db);
  const familyRef = doc(db, 'families', id);

  // Build the Firestore update payload
  const update: Record<string, unknown> = {
    updatedAt: serverTimestamp(),
    updatedBy: actorName,
  };
  if (patch.name !== undefined) update.name = patch.name;
  if (patch.type !== undefined) update.type = patch.type;
  if (patch.description !== undefined) update.description = patch.description;
  if (patch.members !== undefined) update.members = patch.members;
  // Primary location — trimmed; an empty string CLEARS it (never `undefined`).
  if (patch.location !== undefined) update.location = patch.location.trim();
  // Billing contact FK → clients/{cid} — trimmed; empty string CLEARS it.
  if (patch.billingContactId !== undefined) {
    update.billingContactId = patch.billingContactId.trim();
  }
  // Org-billing fields — write each provided key verbatim (empty string CLEARS
  // it; never `undefined`, which Firestore would reject on this db).
  for (const k of ORG_BILLING_KEYS) {
    if (patch[k] !== undefined) update[k] = (patch[k] as string).trim();
  }

  batch.update(familyRef, update);

  // Sync familyIds on contacts if members changed
  if (patch.members !== undefined) {
    const prevIds = new Set(prevMembers.map((m) => m.contactId));
    const nextIds = new Set(patch.members.map((m) => m.contactId));

    // Added contacts
    for (const m of patch.members) {
      if (!prevIds.has(m.contactId)) {
        const p = { familyIds: arrayUnion(id) };
        batch.set(doc(db, 'clients', m.contactId), p, { merge: true });
      }
    }
    // Removed contacts
    for (const m of prevMembers) {
      if (!nextIds.has(m.contactId)) {
        const p = { familyIds: arrayRemove(id) };
        batch.set(doc(db, 'clients', m.contactId), p, { merge: true });
      }
    }
  }

  await batch.commit();
}

/**
 * Add a single member to an existing FamilyGroup (array-union on `members`).
 * Also adds the familyId to the contact's `familyIds`.
 */
export async function addMemberToFamily(
  familyId: string,
  member: FamilyMember,
  actorName: string,
): Promise<void> {
  const batch = writeBatch(db);

  // Firestore arrayUnion on a map checks deep equality — we use a replace
  // pattern via the full members read inside updateFamily instead. Here we
  // use a direct update because this is an incremental add.
  const familyRef = doc(db, 'families', familyId);
  batch.update(familyRef, {
    members: arrayUnion(member),
    updatedAt: serverTimestamp(),
    updatedBy: actorName,
  });

  // Back-fill familyIds on the contact (Build I — clients/ is the one mirror)
  const addP = { familyIds: arrayUnion(familyId) };
  batch.set(doc(db, 'clients', member.contactId), addP, { merge: true });

  await batch.commit();
}

/**
 * Remove a member from a FamilyGroup by contactId. Also removes the familyId
 * from the contact's `familyIds`.
 *
 * NOTE: Firestore `arrayRemove` on a map uses deep equality. Because the
 * member map also carries `name`/`relationship` which may differ from what
 * is stored, this function fetches the current members list and rebuilds it
 * (filtering out the contactId) rather than relying on arrayRemove on the
 * full member object.
 */
export async function removeMemberFromFamily(
  familyId: string,
  contactId: string,
  currentMembers: FamilyMember[],
  actorName: string,
): Promise<void> {
  const newMembers = currentMembers.filter((m) => m.contactId !== contactId);
  const batch = writeBatch(db);

  batch.update(doc(db, 'families', familyId), {
    members: newMembers,
    updatedAt: serverTimestamp(),
    updatedBy: actorName,
  });

  const remP = { familyIds: arrayRemove(familyId) };
  batch.set(doc(db, 'clients', contactId), remP, { merge: true });

  await batch.commit();
}

/**
 * Delete a FamilyGroup entirely. Removes the familyId from every member's
 * `familyIds` array in a batch.
 *
 * LINK FAN-OUT (owner bug 2026-07-03): a deleted Group must not stay any
 * project's astro subject. Every project whose `linked_group_id` points at this
 * Group gets the SAME clear `setProjectLinkedGroup(null)` writes
 * (`projectLinkedGroupBody` — one shared definition): `linked_group_id` null +
 * the LEGACY `astro_family_id` / `astro_person_ids` nulled, so the backend
 * resolver's back-compat cascade can't resurrect the old family on the
 * Astro-Vastu tab. BEST-EFFORT: a fan-out failure is console.warn'd and the
 * delete still proceeds (the owner's delete must never be blocked by a stale
 * link — the Astro-Vastu link panels can re-clear manually).
 */
export async function deleteFamily(
  familyId: string,
  currentMembers: FamilyMember[],
): Promise<void> {
  // 1. Best-effort project-link fan-out (own batch, so a failure here cannot
  //    poison the delete batch below).
  try {
    const snap = await getDocs(
      query(collection(db, 'projects'), where('linked_group_id', '==', familyId)),
    );
    if (!snap.empty) {
      const fanout = writeBatch(db);
      for (const d of snap.docs) {
        fanout.update(d.ref, {
          ...projectLinkedGroupBody(null),
          updatedAt: serverTimestamp(),
        });
      }
      await fanout.commit();
    }
  } catch (e) {
    console.warn(
      `[deleteFamily] project-link fan-out failed for ${familyId} (links may be stale):`,
      (e as Error).message,
    );
  }

  // 2. The delete itself (member back-refs + the doc), unchanged.
  const batch = writeBatch(db);

  // Remove from each contact's familyIds (Build I — clients/ is the one mirror)
  for (const m of currentMembers) {
    const p = { familyIds: arrayRemove(familyId) };
    batch.set(doc(db, 'clients', m.contactId), p, { merge: true });
  }

  // Delete the doc
  batch.delete(doc(db, 'families', familyId));

  await batch.commit();
}

/**
 * Dedup suppression (P1 Contacts, 2026-07-06) — mark two Groups (two families
 * or two organisations) as CONFIRMED-DISTINCT ("not a duplicate — keep both").
 * Writes each group's id into the OTHER's `notDuplicateOf[]` (arrayUnion) in one
 * batch, symmetric, so the Duplicates tab never re-flags the pair. Neither doc
 * is otherwise changed.
 */
export async function markGroupsNotDuplicate(
  idA: string,
  idB: string,
  actorName: string,
): Promise<void> {
  const batch = writeBatch(db);
  batch.set(
    doc(db, 'families', idA),
    { notDuplicateOf: arrayUnion(idB), updatedAt: serverTimestamp(), updatedBy: actorName },
    { merge: true },
  );
  batch.set(
    doc(db, 'families', idB),
    { notDuplicateOf: arrayUnion(idA), updatedAt: serverTimestamp(), updatedBy: actorName },
    { merge: true },
  );
  await batch.commit();
}

/* ═══════════════════════ Recoverable soft-delete + merge (Groups) ═══════════
 *
 * Owner-approved (P1 Contacts, 2026-07-06). Two capabilities that close the
 * Duplicates tab's group gap WITHOUT the destructive hard `deleteFamily`:
 *
 *   · trashFamily / restoreFamily — flip `trashed` (mirrors the contact
 *     soft-delete). A trashed Group is filtered out of `listenFamilies`, so it
 *     disappears from Groups / pickers / dedup / project links, yet the doc
 *     survives and is restorable. Duplicate "Delete one" wires here.
 *   · mergeFamilyInto — union the loser's roster onto the survivor, fill the
 *     survivor's empty billing/org fields from the loser, re-point every
 *     project that pointed at the loser Group, then soft-trash the loser — all
 *     in ONE atomic batch. Duplicate "Merge" wires here.
 *
 * Both are soft-delete only: a Group is NEVER hard-destroyed by these.
 * ────────────────────────────────────────────────────────────────────────── */

/** PURE — the doc body a group trash / restore write sends. */
export function familyTrashBody(
  trashed: boolean,
  actorName: string,
): Record<string, unknown> {
  return {
    trashed,
    trashedBy: trashed ? actorName : null,
    updatedBy: actorName,
  };
}

/**
 * Send a Group to the recoverable Trash (or restore it). Mirrors `trashClient`:
 * an ordinary `trashed` field write (`families/` `allow update: if isTeam()`
 * already permits it — no rules change). On TRASH it also runs the SAME
 * best-effort project-link fan-out `deleteFamily` uses (a trashed Group must not
 * stay any project's astro subject); a fan-out failure is warned, never thrown —
 * the soft-delete itself has already landed and is recoverable. Restore only
 * flips the flag back (project links are re-attached manually, exactly as a
 * contact restore does).
 */
export async function trashFamily(
  familyId: string,
  actorName: string,
  trashed: boolean,
): Promise<void> {
  await setDoc(
    doc(db, 'families', familyId),
    {
      ...familyTrashBody(trashed, actorName),
      trashedAt: trashed ? serverTimestamp() : null,
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );

  if (trashed) {
    try {
      const snap = await getDocs(
        query(collection(db, 'projects'), where('linked_group_id', '==', familyId)),
      );
      if (!snap.empty) {
        const fanout = writeBatch(db);
        for (const d of snap.docs) {
          fanout.update(d.ref, {
            ...projectLinkedGroupBody(null),
            updatedAt: serverTimestamp(),
          });
        }
        await fanout.commit();
      }
    } catch (e) {
      console.warn(
        `[trashFamily] project-link fan-out failed for ${familyId} (links may be stale):`,
        (e as Error).message,
      );
    }
  }
}

/** Convenience — restore a soft-trashed Group (clears the `trashed` flag). */
export async function restoreFamily(familyId: string, actorName: string): Promise<void> {
  await trashFamily(familyId, actorName, false);
}

const asTrimmed = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

/**
 * Merge one Group (`loserId`) INTO another (`survivorId`):
 *   · roster  — union of members, deduped by contactId; the SURVIVOR's member
 *     entry wins on any overlap (its relationship / org-axis is kept);
 *   · billing — the survivor keeps its own org-billing values, and only the
 *     EMPTY ones are back-filled from the loser (never overwrites survivor data);
 *   · projects — every project whose `linked_group_id` (or legacy
 *     `astro_family_id`) points at the loser Group is re-pointed to the survivor;
 *   · the loser Group is SOFT-trashed (recoverable), stamped `mergedIntoFamilyId`.
 *
 * All writes ride ONE atomic batch (soft-trash included), so the merge is
 * all-or-nothing. Guards: refuses a self-merge, a missing group, and a
 * cross-kind merge (a family can never fold into an organisation, or vice-versa).
 *
 * NOTE on denorm — newly-inherited members gain the survivor's id on their
 * `familyIds`; the loser's id is intentionally LEFT on member docs (harmless:
 * the trashed loser Group is filtered out of every listener, so it resolves to
 * nothing — the same choice `trashFamily` makes). This keeps each contact doc
 * written at most once (no arrayUnion/arrayRemove clash on the same field).
 */
export async function mergeFamilyInto(
  loserId: string,
  survivorId: string,
  actorName: string,
): Promise<{ membersAdded: number; projectsRepointed: number }> {
  if (!loserId || !survivorId) {
    throw new Error('Group merge needs both a loser and a survivor group.');
  }
  if (loserId === survivorId) {
    throw new Error('Cannot merge a group into itself.');
  }

  const [loserSnap, survivorSnap] = await Promise.all([
    getDoc(doc(db, 'families', loserId)),
    getDoc(doc(db, 'families', survivorId)),
  ]);
  if (!loserSnap.exists() || !survivorSnap.exists()) {
    throw new Error('One of the groups no longer exists — refresh and try again.');
  }
  const loser = loserSnap.data() as Record<string, unknown>;
  const survivor = survivorSnap.data() as Record<string, unknown>;

  const loserType = asTrimmed(loser.type) || 'family';
  const survivorType = asTrimmed(survivor.type) || 'family';
  if (loserType !== survivorType) {
    throw new Error(
      `Cannot merge a ${loserType} into a ${survivorType} — the two groups are different kinds.`,
    );
  }

  const survivorMembers: FamilyMember[] = Array.isArray(survivor.members)
    ? (survivor.members as FamilyMember[])
    : [];
  const loserMembers: FamilyMember[] = Array.isArray(loser.members)
    ? (loser.members as FamilyMember[])
    : [];
  const survivorIds = new Set(survivorMembers.map((m) => m.contactId));
  // Survivor wins on overlap → only the loser-ONLY members are appended.
  const added = loserMembers.filter((m) => !survivorIds.has(m.contactId));
  const mergedMembers = [...survivorMembers, ...added];

  // Org-billing back-fill — only fill the survivor's EMPTY keys from the loser.
  const billingPatch: Record<string, string> = {};
  if (survivorType === 'organization') {
    for (const k of ORG_BILLING_KEYS) {
      if (!asTrimmed(survivor[k]) && asTrimmed(loser[k])) billingPatch[k] = asTrimmed(loser[k]);
    }
  }

  // Astro link — route the loser's astroFamilyId to the survivor so FUTURE
  // astro re-syncs update the survivor, not the trashed loser (Vex 4b). Only
  // when the survivor has none of its own; if BOTH carry one (rare) keep the
  // survivor's and leave the loser's alone (it is trashed + filtered out).
  const loserAstroFamilyId = asTrimmed(loser.astroFamilyId);
  const survivorOwnAstroId = asTrimmed(survivor.astroFamilyId);
  const inheritsAstro = Boolean(loserAstroFamilyId) && !survivorOwnAstroId;
  const astroLinkPatch: Record<string, unknown> = inheritsAstro
    ? { astroFamilyId: loserAstroFamilyId, astroMemberCount: mergedMembers.length }
    : {};
  // The survivor's astroFamilyId AFTER the merge — its own, else the inherited
  // loser id, else none. Projects pointing at the loser's astro link re-point
  // here (a no-op value when inherited, since the survivor now owns that id).
  const effectiveSurvivorAstroId = survivorOwnAstroId || (inheritsAstro ? loserAstroFamilyId : '') || null;

  const now = serverTimestamp();
  const batch = writeBatch(db);

  // 1. Survivor — merged roster + billing back-fill + inherited astro link.
  batch.update(doc(db, 'families', survivorId), {
    members: mergedMembers,
    ...billingPatch,
    ...astroLinkPatch,
    updatedAt: now,
    updatedBy: actorName,
  });

  // 2. Denorm — inherited members gain the survivor Group id.
  for (const m of added) {
    batch.set(doc(db, 'clients', m.contactId), { familyIds: arrayUnion(survivorId) }, { merge: true });
  }

  // 3. Project re-point — collect into a map so a project matched by BOTH the
  //    portal link AND the legacy astro link is written once.
  const projPatch = new Map<string, Record<string, unknown>>();
  const groupLinkSnap = await getDocs(
    query(collection(db, 'projects'), where('linked_group_id', '==', loserId)),
  );
  for (const d of groupLinkSnap.docs) {
    projPatch.set(d.id, { ...(projPatch.get(d.id) ?? {}), linked_group_id: survivorId });
  }
  if (loserAstroFamilyId) {
    const astroLinkSnap = await getDocs(
      query(collection(db, 'projects'), where('astro_family_id', '==', loserAstroFamilyId)),
    );
    for (const d of astroLinkSnap.docs) {
      projPatch.set(d.id, {
        ...(projPatch.get(d.id) ?? {}),
        astro_family_id: effectiveSurvivorAstroId,
      });
    }
  }
  for (const [pid, patch] of projPatch) {
    batch.update(doc(db, 'projects', pid), { ...patch, updatedAt: now });
  }

  // 4. Soft-trash the loser Group (recoverable) + merge audit stamp.
  batch.set(
    doc(db, 'families', loserId),
    {
      trashed: true,
      trashedBy: actorName,
      trashedAt: now,
      mergedIntoFamilyId: survivorId,
      updatedBy: actorName,
      updatedAt: now,
    },
    { merge: true },
  );

  await batch.commit();
  return { membersAdded: added.length, projectsRepointed: projPatch.size };
}
